import { useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * Custom hook to handle Chrome Extension authentication flow
 * 
 * This hook:
 * 1. Detects when the page is accessed via Chrome Extension auth
 * 2. Stores extension auth data for post-login redirect
 * 3. Handles the redirect back to extension after successful login
 */
export const useExtensionAuthFlow = () => {
  const { getToken, isSignedIn } = useAuth();

  // Detect and validate extension auth parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isExtensionAuth = urlParams.get('extension_auth') === 'true';
    const encodedState = urlParams.get('state');
    const redirectUrl = urlParams.get('redirect_url');

    if (isExtensionAuth && encodedState && redirectUrl) {
      try {
        // Decode and validate state
        const stateData = JSON.parse(atob(encodedState));
        
        // Validate source and expiration
        if (stateData.source === "chrome_extension") {
          const maxAge = 10 * 60 * 1000; // 10 minutes
          
          if (Date.now() - stateData.ts <= maxAge) {
            // Store for post-login redirect
            sessionStorage.setItem('extensionAuth', JSON.stringify({
              redirectUrl: redirectUrl,
              state: encodedState,
              nonce: stateData.nonce,
              timestamp: stateData.ts
            }));
            
            console.log('🚗 Chrome Extension auth detected:', {
              nonce: stateData.nonce,
              age: Math.round((Date.now() - stateData.ts) / 1000) + 's',
              redirectUrl: redirectUrl
            });
          } else {
            console.error('❌ Extension auth request expired');
            window.location.href = `${redirectUrl}?error=request_expired&state=${encodedState}`;
          }
        } else {
          console.error('❌ Invalid extension auth source:', stateData.source);
          window.location.href = `${redirectUrl}?error=invalid_source&state=${encodedState}`;
        }
      } catch (error) {
        console.error('❌ Invalid extension auth state:', error);
        window.location.href = `${redirectUrl}?error=invalid_state&state=${encodedState}`;
      }
    }
  }, []);

  // Handle successful authentication redirect for extension
  useEffect(() => {
    if (isSignedIn) {
      const extensionAuth = sessionStorage.getItem('extensionAuth');
      
      if (extensionAuth) {
        const handleExtensionRedirect = async () => {
          try {
            const { redirectUrl, state, nonce } = JSON.parse(extensionAuth);
            const token = await getToken({ template: "default" });
            
            if (!token) {
              throw new Error('Failed to generate authentication token');
            }
            
            console.log('🔄 Redirecting to Chrome Extension:', {
              nonce: nonce,
              hasToken: true,
              tokenLength: token.length
            });
            
            // Clear stored data before redirect
            sessionStorage.removeItem('extensionAuth');
            
            // Redirect back to extension with token
            window.location.href = `${redirectUrl}?token=${token}&state=${state}`;
          } catch (error) {
            console.error('❌ Extension redirect error:', error);
            
            // Clean up and redirect with error
            const { redirectUrl, state } = JSON.parse(extensionAuth);
            sessionStorage.removeItem('extensionAuth');
            window.location.href = `${redirectUrl}?error=token_generation_failed&state=${state}`;
          }
        };

        handleExtensionRedirect();
      }
    }
  }, [isSignedIn, getToken]);

  // Utility function to handle auth errors for extension users
  const handleExtensionAuthError = (error) => {
    const extensionAuth = sessionStorage.getItem('extensionAuth');
    
    if (extensionAuth) {
      try {
        const { redirectUrl, state } = JSON.parse(extensionAuth);
        sessionStorage.removeItem('extensionAuth');
        
        const errorMessage = error?.errors?.[0]?.message || 
                           error?.message || 
                           'Authentication failed';
        
        window.location.href = `${redirectUrl}?error=${encodeURIComponent(errorMessage)}&state=${state}`;
        return true; // Indicates extension error was handled
      } catch (parseError) {
        console.error('❌ Error parsing extension auth data:', parseError);
      }
    }
    
    return false; // No extension auth to handle
  };

  // Check if current request is from extension
  const isExtensionAuthRequest = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('extension_auth') === 'true';
  };

  return {
    handleExtensionAuthError,
    isExtensionAuthRequest
  };
};

export default useExtensionAuthFlow;